/**
 * Webhook Models and Interfaces
 * TypeScript interfaces matching backend webhook schema
 */

/**
 * Webhook Events
 */
export enum WebhookEvent {
  DOCUMENT_UPLOADED = 'document.uploaded',
  DOCUMENT_PARSED = 'document.parsed',
  DOCUMENT_FAILED = 'document.failed',
  DOCUMENT_COMPLETED = 'document.completed'
}

/**
 * Webhook Delivery Status
 */
export enum WebhookDeliveryStatus {
  PENDING = 'pending',
  DELIVERED = 'delivered',
  FAILED = 'failed',
  RETRYING = 'retrying'
}

/**
 * Retry Configuration
 */
export interface RetryConfig {
  max_retries: number;
  retry_intervals: number[]; // in seconds
}

/**
 * Delivery Statistics
 */
export interface DeliveryStatistics {
  total_deliveries: number;
  successful_deliveries: number;
  failed_deliveries: number;
  last_delivery_at?: Date;
  last_success_at?: Date;
  last_failure_at?: Date;
}

/**
 * Webhook Model
 */
export interface Webhook {
  _id: string;
  organization_id: string;
  created_by: string;
  url: string;
  events: WebhookEvent[];
  description?: string;
  is_active: boolean;
  retry_config: RetryConfig;
  statistics: DeliveryStatistics;
  created_at: Date;
  updated_at: Date;
}

/**
 * Webhook Delivery Log
 */
export interface WebhookDelivery {
  _id: string;
  webhook_id: string;
  organization_id: string;
  event_type: WebhookEvent;
  payload: any;
  status: WebhookDeliveryStatus;
  attempt_count: number;
  response_status_code?: number;
  response_body?: string;
  error_message?: string;
  created_at: Date;
  delivered_at?: Date;
  next_retry_at?: Date;
}

/**
 * Request DTOs
 */
export interface CreateWebhookRequest {
  url: string;
  events: WebhookEvent[];
  description?: string;
}

export interface UpdateWebhookRequest {
  url?: string;
  events?: WebhookEvent[];
  description?: string;
  is_active?: boolean;
}

export interface TestWebhookRequest {
  event_type: WebhookEvent;
}

/**
 * Response DTOs
 */
export interface CreateWebhookResponse {
  message: string;
  webhook: Webhook;
  secret: string; // Shown only once!
}

export interface ListWebhooksResponse {
  webhooks: Webhook[];
  total: number;
  page: number;
  page_size: number;
}

export interface ListWebhookDeliveriesResponse {
  deliveries: WebhookDelivery[];
  total: number;
  page: number;
  page_size: number;
}

export interface TestWebhookResponse {
  success: boolean;
  message: string;
  delivery_id?: string;
  delivery_time_ms?: number;
  response_status_code?: number;
  response_body?: string;
  error_message?: string;
}

/**
 * Event Metadata for UI Display
 */
export interface WebhookEventMetadata {
  value: WebhookEvent;
  label: string;
  description: string;
  icon: string;
  color: string;
}

/**
 * Status Metadata for UI Display
 */
export interface DeliveryStatusMetadata {
  value: WebhookDeliveryStatus;
  label: string;
  color: string;
  icon: string;
}

/**
 * Helper Functions
 */

/**
 * Get event metadata for UI display
 */
export function getEventMetadata(event: WebhookEvent): WebhookEventMetadata {
  const metadata: Record<WebhookEvent, WebhookEventMetadata> = {
    [WebhookEvent.DOCUMENT_UPLOADED]: {
      value: WebhookEvent.DOCUMENT_UPLOADED,
      label: 'Document Uploaded',
      description: 'Triggered when a document is successfully uploaded',
      icon: 'cloud_upload',
      color: 'primary'
    },
    [WebhookEvent.DOCUMENT_PARSED]: {
      value: WebhookEvent.DOCUMENT_PARSED,
      label: 'Document Parsed',
      description: 'Triggered when document parsing completes successfully',
      icon: 'check_circle',
      color: 'accent'
    },
    [WebhookEvent.DOCUMENT_FAILED]: {
      value: WebhookEvent.DOCUMENT_FAILED,
      label: 'Document Failed',
      description: 'Triggered when document parsing fails',
      icon: 'error',
      color: 'warn'
    },
    [WebhookEvent.DOCUMENT_COMPLETED]: {
      value: WebhookEvent.DOCUMENT_COMPLETED,
      label: 'Document Completed',
      description: 'Triggered when all document processing is complete',
      icon: 'done_all',
      color: 'primary'
    }
  };
  return metadata[event];
}

/**
 * Get all available events
 */
export function getAllEvents(): WebhookEventMetadata[] {
  return Object.values(WebhookEvent).map(event => getEventMetadata(event));
}

/**
 * Get delivery status metadata for UI display
 */
export function getStatusMetadata(status: WebhookDeliveryStatus): DeliveryStatusMetadata {
  const metadata: Record<WebhookDeliveryStatus, DeliveryStatusMetadata> = {
    [WebhookDeliveryStatus.PENDING]: {
      value: WebhookDeliveryStatus.PENDING,
      label: 'Pending',
      color: 'accent',
      icon: 'schedule'
    },
    [WebhookDeliveryStatus.DELIVERED]: {
      value: WebhookDeliveryStatus.DELIVERED,
      label: 'Delivered',
      color: 'primary',
      icon: 'check_circle'
    },
    [WebhookDeliveryStatus.FAILED]: {
      value: WebhookDeliveryStatus.FAILED,
      label: 'Failed',
      color: 'warn',
      icon: 'error'
    },
    [WebhookDeliveryStatus.RETRYING]: {
      value: WebhookDeliveryStatus.RETRYING,
      label: 'Retrying',
      color: 'accent',
      icon: 'refresh'
    }
  };
  return metadata[status];
}

/**
 * Calculate success rate percentage
 */
export function calculateSuccessRate(stats: DeliveryStatistics): number {
  if (stats.total_deliveries === 0) return 0;
  return Math.round((stats.successful_deliveries / stats.total_deliveries) * 100);
}

/**
 * Format event name for display
 */
export function formatEventName(event: WebhookEvent): string {
  return getEventMetadata(event).label;
}

/**
 * Format status for display
 */
export function formatStatus(status: WebhookDeliveryStatus): string {
  return getStatusMetadata(status).label;
}

